'use client';

import React, { useState } from 'react';
import { useTranslations } from 'next-intl';
import { QuickCalculatorForm } from '@/components/quick-calculator/QuickCalculatorForm';
import { calculateKaalSarp, KaalSarpResult, CreateBirthProfileInput } from '@/lib/api/client';
import { ShieldCheck, AlertTriangle, Info, Compass } from 'lucide-react';

export interface KaalSarpCalculatorClientProps {
  initialResult?: KaalSarpResult | null;
  initialError?: string | null;
}

export function KaalSarpCalculatorClient({
  initialResult = null,
  initialError = null
}: KaalSarpCalculatorClientProps) {
  const t = useTranslations('kaalSarp');
  const tGlossary = useTranslations('glossary');

  const [result, setResult] = useState<KaalSarpResult | null>(initialResult);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(initialError);

  const handleCalculate = async (input: CreateBirthProfileInput) => {
    setLoading(true);
    setError(null);
    try {
      const data = await calculateKaalSarp(input);
      setResult(data);
    } catch (err: any) {
      setError(err.message || t('noDosh'));
      setResult(null);
    } finally {
      setLoading(false);
    }
  };

  const getTypeName = (typeId?: number | null, fallback?: string | null): string => {
    if (typeId && typeId >= 1 && typeId <= 12) {
      try {
        const val = tGlossary(`kaalSarpTypes.type${typeId}` as any);
        if (val && !val.startsWith('kaalSarpTypes.')) return val;
      } catch (_) {}
    }
    return fallback || '';
  };

  const getHouseOrdinal = (houseNum?: number | null): string => {
    if (!houseNum || houseNum < 1 || houseNum > 12) return String(houseNum || '');
    try {
      const val = tGlossary(`houses.${houseNum}` as any);
      if (val && !val.startsWith('houses.')) return val;
    } catch (_) {}
    return String(houseNum);
  };

  const getPlanetName = (planetKey: string): string => {
    const key = planetKey.toLowerCase();
    try {
      const val = tGlossary(`planets.${key}` as any);
      if (val && !val.startsWith('planets.')) return val;
    } catch (_) {}
    return planetKey;
  };

  const getResultTitle = (res: KaalSarpResult): string => {
    const doshType = getTypeName(res.type_id, res.dosh_type);
    if (res.is_kaal_sarp_dosh) {
      return t('doshPresent', { doshType });
    }
    if (res.is_partial_kaal_sarp) {
      return t('partialPresent', { doshType });
    }
    return t('noDosh');
  };

  const getAlignmentBadge = (res: KaalSarpResult): string => {
    if (res.is_kaal_sarp_dosh) return t('alignmentFull');
    if (res.is_partial_kaal_sarp) return t('alignmentPartial');
    return t('alignmentClear');
  };

  const getResultDescription = (res: KaalSarpResult): string => {
    const doshType = getTypeName(res.type_id, res.dosh_type);
    if (res.is_kaal_sarp_dosh) {
      return t('fullDesc', { doshType });
    }
    if (res.is_partial_kaal_sarp) {
      const outliers = (res.outlier_planets || []).map(p => getPlanetName(p)).join(', ');
      return t('partialDesc', { doshType, outliers });
    }
    return t('clearDesc');
  };

  return (
    <div className="space-y-8" data-testid="kaal-sarp-client">
      <QuickCalculatorForm
        title={t('title')}
        subtitle={t('subtitle')}
        onCalculate={handleCalculate}
        loading={loading}
      />

      {error && (
        <div data-testid="kaal-sarp-error-panel" className="bg-red-500/10 border border-red-500/30 rounded-xl p-4 text-red-300 text-sm">
          {error}
        </div>
      )}

      {result && (
        <div data-testid="kaal-sarp-result-panel" className="space-y-6">
          {/* Main Status Header Card */}
          <div
            className={`border rounded-2xl p-6 sm:p-8 shadow-xl relative overflow-hidden text-center sm:text-left ${
              result.is_kaal_sarp_dosh
                ? 'bg-amber-500/10 border-amber-500/40'
                : result.is_partial_kaal_sarp
                ? 'bg-sky-500/10 border-sky-500/40'
                : 'bg-emerald-500/10 border-emerald-500/40'
            }`}
          >
            <div className="flex flex-col sm:flex-row items-center justify-between gap-6">
              <div className="flex items-center gap-4">
                <div
                  className={`w-16 h-16 rounded-2xl flex items-center justify-center ${
                    result.is_kaal_sarp_dosh
                      ? 'bg-amber-500/20 text-amber-300'
                      : result.is_partial_kaal_sarp
                      ? 'bg-sky-500/20 text-sky-300'
                      : 'bg-emerald-500/20 text-emerald-300'
                  }`}
                >
                  {result.is_kaal_sarp_dosh ? (
                    <AlertTriangle className="w-8 h-8" />
                  ) : result.is_partial_kaal_sarp ? (
                    <Info className="w-8 h-8" />
                  ) : (
                    <ShieldCheck className="w-8 h-8" />
                  )}
                </div>

                <div>
                  <span className="text-xs font-semibold uppercase tracking-wider block text-gray-400">
                    {t('resultHeading')}
                  </span>
                  <h2 className="text-2xl font-serif font-bold text-gray-100 mt-0.5">
                    {getResultTitle(result)}
                  </h2>
                  <span className="text-xs font-mono text-amber-300/90 mt-1 block">
                    {t('rahuHouseFormat', { houseOrdinal: getHouseOrdinal(result.house_of_rahu) })}
                  </span>
                </div>
              </div>

              <div
                className={`px-4 py-2 rounded-full border text-xs font-bold uppercase tracking-wider ${
                  result.is_kaal_sarp_dosh
                    ? 'bg-amber-500/20 text-amber-300 border-amber-500/40'
                    : result.is_partial_kaal_sarp
                    ? 'bg-sky-500/20 text-sky-300 border-sky-500/40'
                    : 'bg-emerald-500/20 text-emerald-300 border-emerald-500/40'
                }`}
              >
                {getAlignmentBadge(result)}
              </div>
            </div>

            <p className="text-sm text-gray-300 leading-relaxed mt-6 border-t border-gray-800/80 pt-4">
              {getResultDescription(result)}
            </p>

            {result.outlier_planets && result.outlier_planets.length > 0 && (
              <div className="mt-3 text-xs text-sky-300 font-mono bg-sky-950/40 border border-sky-800/50 rounded-lg p-2.5">
                {t('outliersLabel', {
                  planets: result.outlier_planets.map(p => getPlanetName(p)).join(', ')
                })}
              </div>
            )}
          </div>

          {/* Classical & Remedial Perspective Card */}
          <div className="bg-astro-900 border border-gray-800 rounded-2xl p-6 shadow-xl space-y-3">
            <h3 className="text-base font-serif font-bold text-amber-400 flex items-center gap-2">
              <Compass className="w-4 h-4 text-amber-400" />
              <span>{t('perspectiveTitle')}</span>
            </h3>
            <p className="text-xs text-gray-300 leading-relaxed">
              {t('perspectiveBody')}
            </p>
            <div className="bg-astro-950/60 border border-gray-800 rounded-xl p-3.5 text-xs text-gray-400 italic">
              {t('remedyNote')}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
